import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/router/routeNames'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'

// Middleware for app routes - redirects to auth if user fetch fails or email not verified
export default function auth({ to, next }: { to: RouteLocationNormalized, next: NavigationGuardNext }) {
  const authStore = useAuthStore()

  // Skip email verification check for these routes
  const skipEmailVerificationCheck = [
    ROUTES.EMAIL_VERIFICATION,
    ROUTES.PROFILE,
    ROUTES.LOGOUT
  ]

  // Skip bank account check for these routes
  const skipBankAccountCheck = [
    ROUTES.PROFILE,
    ROUTES.LOGOUT,
    ROUTES.EMAIL_VERIFICATION
  ]

  return authStore.getUser()
    .then(() => {
      const user = authStore.user;

      // If user is authenticated but email is not verified
      // and the route is not in the skip list, redirect to email verification
      if (user && !user.email_verified_at && !skipEmailVerificationCheck.includes(to.name as string)) {
        return next({ name: ROUTES.EMAIL_VERIFICATION })
      }

      // Only redirect to profile if user needs a bank account but doesn't have one
      // Business and delivery_driver users need bank accounts, customers and admins don't
      const needsBankAccount = user && (user.role === 'business' || user.role === 'delivery_driver')
      if (needsBankAccount && !user.bank_account && !skipBankAccountCheck.includes(to.name as string)) {
        return next({ name: ROUTES.PROFILE })
      }

      // User is authenticated and email is verified, proceed to the requested route
      next()
    })
    .catch(() => {
      // User is not authenticated, redirect to login page directly
      // Store the intended destination for post-login redirect
      next({
        name: ROUTES.LOGIN,
        query: { redirect: to.fullPath }
      })
    })
}
